import { useEffect, useState } from 'react';
import {
  Alert,Button, Box, Card, CardContent, Chip, FormControlLabel, MenuItem, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Typography, alpha,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useToast } from '../../components/ToastProvider';
import { errMessage } from '../../utils/errMessage';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { TradingViewChart } from '../../components/market/TradingViewChart';
import { OrderBook } from '../../components/market/OrderBook';
import { useBinanceTicker, TOP_SYMBOLS, fmtPrice } from '../../hooks/useMarketData';

type Market = 'SPOT' | 'FUTURES';
type Side = 'BUY' | 'SELL';

interface Account { id: number; provider: string; label: string | null; status: string; access_mode: string | null; }

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Typography className="mono" sx={{ fontWeight: 700, fontSize: 14, color }}>{value}</Typography>
    </Box>
  );
}

export default function TradePage() {
  const [params, setParams] = useSearchParams();
  const initialSymbol = (params.get('symbol') || 'BTCUSDT').toUpperCase();
  const [symbol, setSymbol] = useState(initialSymbol);
  const [market, setMarket] = useState<Market>('SPOT');
  const [side, setSide] = useState<Side>('BUY');
  // Platform policy: only LIMIT orders are accepted, so the order type is fixed.
  const type = 'LIMIT' as const;
  const [exchange, setExchange] = useState<string>('');
  // Numeric fields are plain text (no native number input) and hold strings so the
  // user can clear them (no forced "0"). They start EMPTY so nothing is pre-filled
  // on load — in particular, Value (USDT) stays blank until a quantity is entered,
  // rather than auto-computing from a default quantity × live price.
  const [form, setForm] = useState({
    accountId: 0, quantity: '', amountUsdt: '', price: '', leverage: '1', reduceOnly: false, takeProfit: '', stopLoss: '',
  });
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  // Accept only a positive-decimal-in-progress (digits with at most one dot) or
  // empty — keeps stray characters, signs and exponents out of numeric fields.
  const DEC_RE = /^\d*\.?\d*$/;
  // A guarded onChange for a plain numeric field: ignores input that isn't a valid
  // decimal so the field can't hold garbage.
  const setNum = (k: string) => (e: { target: { value: string } }) => {
    const v = e.target.value;
    if (DEC_RE.test(v)) set(k, v);
  };

  const ticker = useBinanceTicker(symbol);
  const up = (ticker?.changePct ?? 0) >= 0;

  // TradingView only has clean USDT-pair charts for spot/linear venues; map the
  // selected account's exchange onto a supported prefix (Deribit perps differ).
  const chartExchange = exchange === 'BINANCE' ? 'BINANCE' : 'BYBIT';

  // Price used to convert between USDT value and quantity: the entered limit
  // price, falling back to the live mark until one is typed.
  const base = symbol.replace('USDT', '');
  const markPrice = ticker?.price ?? 0;
  const effPrice = Number(form.price) || markPrice;
  const round8 = (n: number) => Math.round(n * 1e8) / 1e8;
  const round2 = (n: number) => Math.round(n * 1e2) / 1e2;

  // Quantity and USDT value are linked: editing one recomputes the other at the
  // effective price. Both validate as decimals; the order submits the base quantity.
  const setQuantity = (v: string) => {
    if (!DEC_RE.test(v)) return;
    const q = Number(v);
    setForm((f) => ({ ...f, quantity: v, amountUsdt: effPrice > 0 && v !== '' ? String(round2(q * effPrice)) : f.amountUsdt }));
  };
  const setAmountUsdt = (v: string) => {
    if (!DEC_RE.test(v)) return;
    const amt = Number(v);
    setForm((f) => ({ ...f, amountUsdt: v, quantity: effPrice > 0 && v !== '' ? String(round8(amt / effPrice)) : f.quantity }));
  };
  const computedQty = Number(form.quantity);
  // A limit order rests on the book, so it always needs a positive limit price.
  const priceValid = Number(form.price) > 0;
  // Only surface the "price required" error once the field has been touched (typed
  // in or blurred) or a submit was attempted — not on the initial empty load.
  const [priceTouched, setPriceTouched] = useState(false);
  const showPriceError = priceTouched && !priceValid;

  // Keep the USDT field consistent when the effective price moves (live quote or
  // limit-price edits), anchoring on the entered quantity.
  useEffect(() => {
    if (effPrice > 0) setForm((f) => ({ ...f, amountUsdt: f.quantity !== '' ? String(round2(Number(f.quantity) * effPrice)) : f.amountUsdt }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effPrice]);

  const { data: accounts } = useQuery({
    queryKey: ['client-accounts'],
    queryFn: async () => (await api.get('/trading/accounts')).data.data.items as Account[],
  });

  // Deribit has its own dedicated section (its instruments/prices differ), so it's
  // excluded from this generic ticket — only Binance/Bybit accounts are offered here.
  const tradeAccounts = (accounts ?? []).filter((a) => a.provider.toUpperCase() !== 'DERIBIT');
  const hasOnlyDeribit = !!accounts && accounts.length > 0 && tradeAccounts.length === 0;

  // Cascading selection: exchange -> account within that exchange.
  const providers = [...new Set(tradeAccounts.map((a) => a.provider))];
  const acctsForExchange = tradeAccounts.filter((a) => a.provider === exchange);
  const selectedAccount = tradeAccounts.find((a) => a.id === form.accountId) ?? null;
  const isDeribit = false; // Deribit is never selectable here — it lives in its own section.
  // DIRECT accounts execute immediately; APPROVAL accounts queue for admin approval.
  const isDirect = selectedAccount?.access_mode === 'DIRECT';

  // Default to the first exchange + its first account once loaded.
  useEffect(() => {
    if (tradeAccounts.length && !exchange) {
      setExchange(tradeAccounts[0].provider);
      set('accountId', tradeAccounts[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const changeExchange = (prov: string) => {
    setExchange(prov);
    const first = tradeAccounts.find((a) => a.provider === prov);
    set('accountId', first?.id ?? 0);
  };

  const changeSymbol = (s: string) => {
    setSymbol(s);
    setParams({ symbol: s }, { replace: true });
  };

  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const place = useMutation({
    mutationFn: () =>
      api.post('/trading/orders', {
        accountId: Number(form.accountId), market, symbol, side, type,
        quantity: computedQty,
        price: Number(form.price),
        ...(market === 'FUTURES' && {
          positionSide: side === 'BUY' ? 'LONG' : 'SHORT',
          reduceOnly: form.reduceOnly,
          // Deribit doesn't take per-order leverage or attached TP/SL yet.
          ...(!isDeribit && {
            leverage: Number(form.leverage),
            takeProfit: form.takeProfit ? Number(form.takeProfit) : undefined,
            stopLoss: form.stopLoss ? Number(form.stopLoss) : undefined,
          }),
        }),
      }),
    onSuccess: () => toast.success(`${symbol} ${side} ${type} ${isDirect ? 'submitted — executing now' : 'submitted — pending admin approval'}`, 'Order submitted'),
    onError: (e) => toast.error(errMessage(e, 'Failed to submit order'), 'Order failed'),
    onSettled: () => setConfirmOpen(false),
  });
  const result = place.data?.data?.data;
  const buy = side === 'BUY';
  const sideColor = buy ? '#16c784' : '#ea3943';
  const noAccounts = !!accounts && tradeAccounts.length === 0;
  const hasDeribit = (accounts ?? []).some((a) => a.provider.toUpperCase() === 'DERIBIT');

  return (
    <Box>
      <PageHeader title="Trade" subtitle="Live market data — spot & futures order ticket" />

      {hasDeribit && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" component={RouterLink} to="/trade/deribit">Open Deribit ticket</Button>}
        >
          {hasOnlyDeribit
            ? 'Your Deribit account is traded in the dedicated Deribit section — this ticket covers Binance & Bybit.'
            : 'Deribit lists its own instruments (e.g. BTC_USDC-PERPETUAL) and prices — trade it in the dedicated Deribit section.'}
        </Alert>
      )}

      {/* Symbol + live price header */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', py: 2 }}>
          <TextField
            select
            size="small"
            value={symbol}
            onChange={(e) => changeSymbol(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            {TOP_SYMBOLS.map((s) => (
              <MenuItem key={s} value={s}>{s.replace('USDT', '')}/USDT</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography className="mono" sx={{ fontWeight: 800, fontSize: 24, color: up ? '#16c784' : '#ea3943' }}>
              {fmtPrice(ticker?.price)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', color: up ? '#16c784' : '#ea3943', fontWeight: 700 }}>
              {up ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              {ticker ? `${up ? '+' : ''}${ticker.changePct.toFixed(2)}%` : '—'}
            </Box>
          </Box>
          <Stat label="24h High" value={fmtPrice(ticker?.high)} />
          <Stat label="24h Low" value={fmtPrice(ticker?.low)} />
          <Stat label="24h Volume" value={ticker ? `${(ticker.quoteVol / 1e6).toFixed(1)}M` : '—'} />
        </CardContent>
      </Card>

      {/* Chart + order book + ticket */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Card sx={{ flex: '2 1 420px', minWidth: 300 }}>
          <CardContent sx={{ p: 1 }}>
            <TradingViewChart symbol={symbol} exchange={chartExchange} height={460} />
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 240px', minWidth: 240 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ px: 1, mb: 1 }}>Order Book</Typography>
            <OrderBook symbol={symbol} onPick={(p) => set('price', String(p))} />
          </CardContent>
        </Card>

        {/* Order ticket */}
        <Card sx={{ flex: '1 1 300px', minWidth: 280 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2.5 }}>
            <ToggleButtonGroup exclusive fullWidth size="small" value={market} onChange={(_, v) => v && setMarket(v)}>
              <ToggleButton value="SPOT">Spot</ToggleButton>
              <ToggleButton value="FUTURES">Futures</ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              exclusive fullWidth value={side} onChange={(_, v) => v && setSide(v)}
              sx={{
                '& .Mui-selected[value="BUY"]': { color: '#fff', backgroundColor: alpha('#16c784', 0.22), borderColor: alpha('#16c784', 0.6), '&:hover': { backgroundColor: alpha('#16c784', 0.3) } },
                '& .Mui-selected[value="SELL"]': { color: '#fff', backgroundColor: alpha('#ea3943', 0.22), borderColor: alpha('#ea3943', 0.6), '&:hover': { backgroundColor: alpha('#ea3943', 0.3) } },
              }}
            >
              <ToggleButton value="BUY">{market === 'FUTURES' ? 'Long' : 'Buy'}</ToggleButton>
              <ToggleButton value="SELL">{market === 'FUTURES' ? 'Short' : 'Sell'}</ToggleButton>
            </ToggleButtonGroup>

            {/* Exchange -> Account cascade (handles multiple accounts per exchange) */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                select size="small" label="Exchange" value={exchange || ''}
                onChange={(e) => changeExchange(e.target.value)}
                disabled={noAccounts} fullWidth
              >
                {providers.map((prov) => (
                  <MenuItem key={prov} value={prov}>{prov}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Account" value={form.accountId || ''}
                onChange={(e) => set('accountId', Number(e.target.value))}
                disabled={noAccounts || !exchange} fullWidth
              >
                {acctsForExchange.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.label || `Account #${a.id}`}</MenuItem>
                ))}
              </TextField>
            </Box>
            {noAccounts && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1 }}>
                No exchange accounts assigned yet — ask your admin.
              </Typography>
            )}
            {selectedAccount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -1 }}>
                {selectedAccount.access_mode === 'DIRECT' ? (
                  <Chip size="small" label="Direct — no approval"
                    sx={{ color: '#16c784', backgroundColor: alpha('#16c784', 0.14), border: `1px solid ${alpha('#16c784', 0.35)}` }} />
                ) : (
                  <Chip size="small" label="Approval Required"
                    sx={{ color: '#f5a623', backgroundColor: alpha('#f5a623', 0.14), border: `1px solid ${alpha('#f5a623', 0.35)}` }} />
                )}
                <Chip size="small" label={selectedAccount.status}
                  sx={{ color: selectedAccount.status === 'CONNECTED' ? '#16c784' : '#8b97b0', backgroundColor: alpha(selectedAccount.status === 'CONNECTED' ? '#16c784' : '#8b97b0', 0.14), border: `1px solid ${alpha(selectedAccount.status === 'CONNECTED' ? '#16c784' : '#8b97b0', 0.35)}` }} />
              </Box>
            )}

            {/* Order type is fixed to LIMIT — the platform only accepts limit orders. */}
            <TextField
              select size="small" label="Order Type" value="LIMIT" disabled
              helperText="Only limit orders are accepted"
            >
              <MenuItem value="LIMIT">Limit</MenuItem>
            </TextField>

            {/* Enter either side — quantity and USDT value stay in sync at the effective price. */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth size="small" label={`Quantity (${base})`}
                value={form.quantity} onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ inputMode: 'decimal' }}
              />
              <TextField
                fullWidth size="small" label="Value (USDT)"
                value={form.amountUsdt} onChange={(e) => setAmountUsdt(e.target.value)}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Box>
            {effPrice <= 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1 }}>
                Waiting for price — enter a limit price or wait for the live quote to convert USDT ⇄ {base}.
              </Typography>
            )}
            <TextField
              size="small" label="Price (USDT)" required placeholder="0.00"
              value={form.price}
              onChange={(e) => { setPriceTouched(true); setNum('price')(e); }}
              onBlur={() => setPriceTouched(true)}
              error={showPriceError}
              helperText={showPriceError ? 'Price is required for limit orders.' : 'Tip: click an order-book price to autofill.'}
              inputProps={{ inputMode: 'decimal' }}
            />

            {market === 'FUTURES' && (
              <>
                {isDeribit ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Deribit trades one-way (net) perpetuals. Hedge mode, per-order leverage and
                    attached take-profit / stop-loss aren't supported.
                  </Alert>
                ) : (
                  <>
                    <TextField size="small" label="Leverage" value={form.leverage} onChange={setNum('leverage')} inputProps={{ inputMode: 'decimal' }} />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField size="small" fullWidth label="Take Profit" value={form.takeProfit} onChange={setNum('takeProfit')} inputProps={{ inputMode: 'decimal' }} />
                      <TextField size="small" fullWidth label="Stop Loss" value={form.stopLoss} onChange={setNum('stopLoss')} inputProps={{ inputMode: 'decimal' }} />
                    </Box>
                  </>
                )}
                <FormControlLabel control={<Switch size="small" checked={form.reduceOnly} onChange={(e) => set('reduceOnly', e.target.checked)} />} label="Reduce Only" />
              </>
            )}

            <Button
              variant="contained" size="large" color={buy ? 'success' : 'error'}
              disabled={place.isPending || noAccounts || !form.accountId || !(computedQty > 0) || !priceValid}
              onClick={() => setConfirmOpen(true)}
              sx={{ py: 1.1, boxShadow: `0 8px 24px ${alpha(sideColor, 0.35)}` }}
            >
              {place.isPending ? 'Submitting…' : `${buy ? 'Buy' : 'Sell'} ${symbol.replace('USDT', '')}`}
            </Button>

            <ConfirmDialog
              open={confirmOpen}
              title={`Confirm ${market === 'FUTURES' ? (buy ? 'Long' : 'Short') : (buy ? 'Buy' : 'Sell')} order`}
              confirmLabel={`Confirm ${buy ? (market === 'FUTURES' ? 'Long' : 'Buy') : (market === 'FUTURES' ? 'Short' : 'Sell')}`}
              confirmColor={buy ? 'success' : 'error'}
              severity="warning"
              loading={place.isPending}
              onCancel={() => setConfirmOpen(false)}
              onConfirm={() => place.mutate()}
              warning={isDirect
                ? 'This account has DIRECT access — the order executes immediately on the live exchange with real funds, no approval required.'
                : 'This will submit the order for admin approval. Once approved it executes on the live exchange with real funds.'}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.75, columnGap: 2 }}>
                <Typography variant="body2" color="text.secondary">Symbol</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{symbol}</Typography>
                <Typography variant="body2" color="text.secondary">Side / Type</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', color: sideColor }}>
                  {(market === 'FUTURES' ? (buy ? 'LONG' : 'SHORT') : side)} · {type}
                </Typography>
                <Typography variant="body2" color="text.secondary">Market</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{market === 'FUTURES' ? 'PERP' : market}</Typography>
                <Typography variant="body2" color="text.secondary">Quantity</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{computedQty} {base}</Typography>
                <Typography variant="body2" color="text.secondary">Est. Value</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">≈ {Number(form.amountUsdt) || 0} USDT</Typography>
                <Typography variant="body2" color="text.secondary">Price</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{Number(form.price) || 0} USDT</Typography>
                {market === 'FUTURES' && !isDeribit && (
                  <>
                    <Typography variant="body2" color="text.secondary">Leverage</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{Number(form.leverage)}x</Typography>
                  </>
                )}
              </Box>
            </ConfirmDialog>

            {result && (
              <Alert severity={result.status === 'PENDING' ? 'info' : 'success'}>
                {result.status === 'PENDING'
                  ? `Order pending admin approval (#${result.pendingId})`
                  : `Order submitted for execution (#${result.orderId})`}
              </Alert>
            )}
            {place.isError && <Alert severity="error">Failed to place order</Alert>}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
